"use client";

import { BsFiles, BsFileZip, BsXLg } from "react-icons/bs";
import { useTranslations } from "next-intl";
import { generateIcon } from "utils";
import { isIosDevice } from "./helpers";
import type { PhotoItemType, VideoItemType } from "./types";

interface IDownloadDialogProps {
  dialogRef: React.RefObject<HTMLDialogElement>;
  downloadableItems: (PhotoItemType | VideoItemType)[];
  handleDownloadSeparately: any;
  handleDownloadAsZip: any;
  canDownloadAsZip?: boolean;
}

export default function DownloadDialog({
  dialogRef,
  downloadableItems = [],
  handleDownloadSeparately,
  handleDownloadAsZip,
  canDownloadAsZip = true,
}: IDownloadDialogProps) {
  const handleClose = () => dialogRef.current?.close();
  const t = useTranslations();

  // iOS saves at most one file per user gesture, so downloading several files
  // separately cannot work there. Only offer it for a single file.
  const separatelyUnavailable =
    isIosDevice() && downloadableItems.length > 1 && canDownloadAsZip;

  return (
    <dialog className="modal" ref={dialogRef}>
      <div className="modal-box max-w-2xl w-full">
        <div className="modal-content relative z-20">
          <div className="flex mb-10">
            <h2 className="text-xl font-semibold">
              {t("Gallery.downloadTitle")} ({downloadableItems.length})
            </h2>
            <button
              className="ml-auto text-gray-700"
              type="button"
              onClick={() => handleClose()}
            >
              {generateIcon(BsXLg, "w-6 h-6")}
            </button>
          </div>

          <div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
              {!separatelyUnavailable && (
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() =>
                    handleDownloadSeparately(downloadableItems, handleClose)
                  }
                >
                  {generateIcon(BsFiles, "w-4 h-4")}
                  {t("Gallery.downloadSeparately")}
                </button>
              )}

              {canDownloadAsZip && (
                <button
                  className="btn btn-primary"
                  type="button"
                  onClick={() => {
                    return handleDownloadAsZip(downloadableItems, handleClose);
                  }}
                >
                  {generateIcon(BsFileZip, "w-4 h-4")}
                  {t("Gallery.downloadAsZip")}
                </button>
              )}
            </div>

            <p className="text-sm text-center text-gray-500">
              {separatelyUnavailable
                ? t("Gallery.downloadIosSingleOnly")
                : t("Gallery.downloadZipHint")}
            </p>
          </div>
        </div>
      </div>

      <form method="dialog" className="modal-backdrop">
        {/* eslint-disable-next-line */}
        <button>close</button>
      </form>
    </dialog>
  );
}
